import { useEffect, useState, useMemo, useRef } from "react";
import {
  fetchAttendance,
  handleCheckIn,
  fetchAttendanceCount,
  fetchEventRundown,
  fetchEventDetail,
  handleConsumptionCheck,
  fetchGuestTypes,
  updateAttendanceGuestType,
} from "../services/api/eventService";
import QRScanner from "../components/QRScanner";
import EventDetailSection from "../components/EventDetailSection";
import CountdownTimer from "../components/CountdownTimer";
import CommonButton from "../components/CommonButton";
import Table from "../components/Table";
import CustomFormGroup from "../components/CustomFormGroup";
import Pagination from "../components/Pagination";
import { FaCheckCircle, FaTimesCircle, FaList, FaSearch, FaUserCheck } from 'react-icons/fa';
import { useLoading } from "../components/LoadingContext";
import MessageModal from '../components/MessageModal';
import Chart from "../components/Chart";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import {
  MdQrCodeScanner,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdDownload,
} from "react-icons/md";
import { ImSpoonKnife } from "react-icons/im";
import { RiCalendarScheduleFill } from "react-icons/ri";

import "../styles/EventAttendance.css";

const EventAttendancePage = () => {
  const [activeTab, setActiveTab] = useState("detail");
  const [attendance, setAttendance] = useState([]);
  const [attendCount, setAttendCount] = useState(0);
  const [rundown, setRundown] = useState([]);
  const [eventDetail, setEventDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [attendanceFilter, setAttendanceFilter] = useState("all");
  const [tableFilters, setTableFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { showLoading, hideLoading } = useLoading();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(true);
  const [showConsumptionScanner, setShowConsumptionScanner] = useState(false);
  const [recentCheckInId, setRecentCheckInId] = useState(null);
  const [recentConsumptionId, setRecentConsumptionId] = useState(null);
  const [guestTypes, setGuestTypes] = useState([]);
  const [updatingGuestTypeId, setUpdatingGuestTypeId] = useState(null);
  const processingQRRef = useRef(false);

  const limitOptions = [
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "50", value: 50 },
    { label: "All", value: "All" },
  ];

  const eventId = "cf9f42b9-8e81-41d6-ac8e-87446526da8d";

  /* ================= USER ROLE ================= */

  const user = JSON.parse(localStorage.getItem("user"));
  const roleId = user?.role_id;
  const canEditGuestType = [1, 2, 4].includes(roleId);

  /* ================= FETCH ALL DATA ================= */

  useEffect(() => {
    if (!eventId) return;
    loadAllData();
  }, [eventId]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadAttendance(),
        loadRundown(),
        loadEventDetail(),
        loadGuestTypes(),
      ]);
    } catch (err) {
      console.error("LOAD ERROR:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAttendance = async () => {
    const data = await fetchAttendance(eventId);
    // console.log(data[0]);
    setAttendance(data || []);

    const count = await fetchAttendanceCount(eventId);
    setAttendCount(count || 0);

    return data || [];
  };

  const loadRundown = async () => {
    const data = await fetchEventRundown(eventId);
    setRundown(data || []);
  };

  const loadEventDetail = async () => {
    const data = await fetchEventDetail(eventId);
    setEventDetail(data);
  };

  const loadGuestTypes = async () => {
    const data = await fetchGuestTypes();
    setGuestTypes(data);
  };

  const handleGuestTypeChange = async (attendanceId, guestTypeId) => {
    const currentAttendance = attendance.find((item) => item.id === attendanceId);

    if (!canEditGuestType || !currentAttendance ||
      currentAttendance.guest_type?.id === guestTypeId) {
      return;
    }

    try {
      setUpdatingGuestTypeId(attendanceId);
      await updateAttendanceGuestType(attendanceId, guestTypeId);
      await loadAttendance();

      setModalMessage("Guest Type berhasil diperbarui.");
      setIsSuccess(true);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Update guest type error:", error);
      setModalMessage(error.message || "Guest Type gagal diperbarui.");
      setIsSuccess(false);
      setIsModalOpen(true);
    } finally {
      setUpdatingGuestTypeId(null);
    }
  };

  /* ================= CHECK-IN ================= */

  const onCheckIn = async (attendanceId) => {
    try {
      showLoading();
      const latest = await fetchAttendance(eventId);
      const selected = latest.find((a) => a.id === attendanceId);

      if (!selected) {
        setModalMessage("Data tidak ditemukan.");
        setIsSuccess(false);
        setIsModalOpen(true);
        return;
      }

      if (selected.is_attend === true) {
        setModalMessage(`${selected.guest?.name} sudah pernah check-in.`);
        setIsSuccess(false);
        setIsModalOpen(true);
        return;
      }

      await handleCheckIn(attendanceId);
      await loadAttendance();

      setRecentCheckInId(attendanceId);
      
      setTimeout(() => {
        setRecentCheckInId(null);
      }, 2000);

      setModalMessage(
        <>
          <strong>{selected.guest?.name || "-"}</strong> berhasil check-in.
          <br />
          <strong>{selected.guest_type?.name || "-"}</strong>
        </>
      );
      setIsSuccess(true);
      setIsModalOpen(true);

    } catch (error) {
      console.error("Check-in error:", error);
      setModalMessage(error.message || "Terjadi kesalahan saat check-in.");
      setIsSuccess(false);
      setIsModalOpen(true);
    } finally {
      hideLoading();
      setShowScanner(false);
    }
  };

  /* ================= QR SCAN ================= */

  const handleQRScan = async (decodedText) => {
    if (!decodedText) {
      setShowScanner(false);
      return;
    }

    try {
      await onCheckIn(decodedText);
    } catch (err) {
      console.error("QR ERROR:", err);

      setModalMessage("Terjadi kesalahan saat scan.");
      setIsSuccess(false);
      setIsModalOpen(true);
    } finally {
      setShowScanner(false);
    }
  };

  /* ================= FORMAT DATA FOR TABLE ================= */

  const formatPhoneNumber = (phone, roleId) => {
      if (!phone) return "-";

      // Role 1 (super admin) dan 2 (admin) dapat melihat nomor lengkap.
      if (roleId === 1 || roleId === 2) return phone;

      const last4 = phone.slice(-4);
      return `***${last4}`;
    };

    const handleLimitChange = (e) => {
    const value = e.target.value;
    setLimit(value === "All" ? "All" : parseInt(value));
    setCurrentPage(1);
  };

  const isAttendTrue = (value) => {
    return value === 1 || value === true || value === "1";
  };

  const handleAttendanceFilter = (filter) => {
    setAttendanceFilter(filter);
    setCurrentPage(1);
  };

  const filteredAttendance = useMemo(() => {
    const keyword = searchTerm.toLowerCase().trim();

    return attendance
      .filter((item) => {
        const attendanceMatch =
          attendanceFilter === "all" ||
          (attendanceFilter === "attend" && isAttendTrue(item.is_attend)) ||
          (attendanceFilter === "notAttend" && !isAttendTrue(item.is_attend));

        if (!attendanceMatch) return false;
        const guestTypeId = tableFilters.guest_type;
        if (guestTypeId && item.guest_type?.id !== guestTypeId) return false;
        if (!keyword) return true;

        const nameMatch = item.guest?.name
          ?.toLowerCase()
          .includes(keyword);

        const phone = item.guest?.phone_number || "";
        const last4 = phone.slice(-4);

        const phoneMatch = last4.includes(keyword);
        const seatMatch = String(item.seat_number ?? "").includes(keyword);

        return nameMatch || phoneMatch || seatMatch;
      })
      .sort((a, b) =>
        (a.guest?.name || "").localeCompare(b.guest?.name || "")
      );
  }, [attendance, attendanceFilter, searchTerm, tableFilters]);

  const paginatedAttendance = useMemo(() => {
    if (limit === "All") return filteredAttendance;

    const start = (currentPage - 1) * limit;
    const end = start + limit;

    return filteredAttendance.slice(start, end);
  }, [filteredAttendance, currentPage, limit]);

  const attendanceDataFormatted = useMemo(() => {
    return paginatedAttendance.map((item, index) => ({
      no:
        limit === "All"
          ? index + 1
          : (currentPage - 1) * limit + index + 1,

      id: item.id,
      guest_name: item.guest?.name || "-",
      phone_number: formatPhoneNumber(
        item.guest?.phone_number,
        roleId
      ),
      guest_type_id: item.guest_type?.id || "",
      guest_type: item.guest_type?.name || "-",
      cluster_code: item.cluster?.cluster_code || "-",
      seat_number: item.seat_number,
      is_attend: item.is_attend,
      is_consumed: item.is_consumed,
      attend_time: item.attend_time,
      highlight: item.id === recentCheckInId
    }));
  }, [
    paginatedAttendance,
    roleId,
    currentPage,
    limit,
    recentCheckInId
  ]);

  const attendanceColumns = [
    {
      key: "no",
      label: "No",
    },
    {
      key: "guest_name",
      label: "Name",
    },
    {
      key: "phone_number",
      label: "Phone",
    },
    {
      key: "guest_type",
      label: "Guest Type",
      filterOptions: guestTypes.map((guestType) => ({
        label: guestType.name,
        value: guestType.id,
      })),
      filterMatch: (row, guestTypeId) => row.guest_type_id === guestTypeId,
      format: (_, row) =>
        canEditGuestType ? (
          <CustomFormGroup
            type="select"
            name={`guest_type-${row.id}`}
            value={row.guest_type_id}
            disabled={updatingGuestTypeId === row.id}
            onChange={(event) => handleGuestTypeChange(row.id, event.target.value)}
            placeholder="Pilih Guest Type"
            options={guestTypes.map((guestType) => ({
              label: guestType.name,
              value: guestType.id,
            }))}
          />
        ) : (
          row.guest_type
        ),
    },
    // {
    //   key: "cluster_code",
    //   label: "Cluster",
    // },
    // {
    //   key: "seat_number",
    //   label: "Seat",
    // },
    {
      key: "is_attend",
      label: "Status",
      format: (value) =>
        isAttendTrue(value) ? (
          <span style={{ color: "green", fontWeight: 600 }}>
            Checked In
          </span>
        ) : (
          <span style={{ color: "red", fontWeight: 600 }}>
            Not Yet
          </span>
        ),
    },
    {
      key: "attend_time",
      label: "Check-in Time",
      format: (value) =>
        value
          ? new Date(value).toLocaleTimeString("id-ID", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-",
    },
    {
      key: "action",
      label: "Action",
      format: (_, row) =>
        isAttendTrue(row.is_attend) ? (
          <MdCheckBox size={22} color="#8F351C" />
        ) : (
          <MdCheckBoxOutlineBlank
            size={22}
            style={{ cursor: "pointer", color: "#8F351C" }}
            onClick={() => onCheckIn(row.id)}
          />
        ),
    },
  ];

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const eventDateTime =
    eventDetail?.date && eventDetail?.event_start
      ? `${eventDetail.date}T${eventDetail.event_start}+07:00`
      : null;

  const handleDownloadExcel = () => {
    // Gunakan data tabel sebelum pagination agar filter aktif dan urutan nama
    // pada file Excel selalu sama dengan yang ditampilkan di tabel.
    const sourceData = filteredAttendance;

    const exportData =
      sourceData.length > 0
        ? sourceData.map((item, index) => ({
            No: index + 1,
            Name: item.guest?.name || "-",
            Phone: formatPhoneNumber(
              item.guest?.phone_number,
              roleId
            ),
            "Guest Type": item.guest_type?.name || "-",
            // Cluster: item.cluster?.cluster_code || "-",
            // Seat: item.seat_number ?? "-",
            Status: isAttendTrue(item.is_attend)
              ? "Checked In"
              : "Not Yet",
            "Check-in Time": item.attend_time
              ? new Date(item.attend_time).toLocaleString("id-ID")
              : "-",
          }))
        : [
            {
              No: "",
              Name: "",
              Phone: "",
              Status: "",
              "Check-in Time": "",
            },
          ];

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    const fileName = `${eventDetail?.name || "Event"}_Attendance.xlsx`;

    saveAs(data, fileName);
  };

  const attendanceSummary = useMemo(() => {
    const totalGuests = attendance.length;

    const totalAttend = attendance.filter(
      (item) => item.is_attend === true
    ).length;

    const totalNotAttend = totalGuests - totalAttend;

    return {
      totalGuests,
      totalAttend,
      totalNotAttend,
    };
  }, [attendance]);

  const handleConsumption = async (attendanceId) => {
    const selected = attendance.find((a) => a.id === attendanceId);

    if (!selected) {
      setModalMessage("Data tamu tidak ditemukan.");
      setIsSuccess(false);
      setIsModalOpen(true);
      return;
    }

    if (!selected.is_attend) {
      setModalMessage(`${selected.guest?.name} belum check-in.`);
      setIsSuccess(false);
      setIsModalOpen(true);
      return;
    }

    if (selected.is_consumed) {
      setModalMessage(`${selected.guest?.name} sudah mengambil konsumsi.`);
      setIsSuccess(false);
      setIsModalOpen(true);
      return;
    }

    await handleConsumptionCheck(attendanceId);
    await loadAttendance();

    setRecentConsumptionId(attendanceId);

    setTimeout(() => {
      setRecentConsumptionId(null);
    }, 2000);

    setModalMessage(`${selected.guest?.name} berhasil mengambil konsumsi.`);
    setIsSuccess(true);
    setIsModalOpen(true);
  };

  const handleConsumptionQR = async (decodedText) => {
    if (processingQRRef.current) return;

    if (!decodedText) {
      setShowConsumptionScanner(false);
      return;
    }

    processingQRRef.current = true;
    setShowConsumptionScanner(false);

    try {
      await handleConsumption(decodedText);
    } catch (err) {
      console.error("QR Consumption ERROR:", err);

      setModalMessage("Terjadi kesalahan saat scan.");
      setIsSuccess(false);
      setIsModalOpen(true);
    } finally {
      setTimeout(() => {
        processingQRRef.current = false;
      }, 1000);
    }
  };

  const consumptionSummary = useMemo(() => {
    const totalGuests = attendance.length;

    const totalConsumed = attendance.filter(
      (item) => item.is_consumed === true
    ).length;

    const totalNotConsumed = totalGuests - totalConsumed;

    return {
      totalGuests,
      totalConsumed,
      totalNotConsumed,
    };
  }, [attendance]);

  const consumptionColumns = [
    {
      key: "no",
      label: "No",
    },
    {
      key: "guest_name",
      label: "Name",
    },
    {
      key: "phone_number",
      label: "Phone",
    },
    {
      key: "is_consumed",
      label: "Consumption",
      format: (value, row) => {
        if (!row.is_attend) {
          return (
            <MdCheckBoxOutlineBlank
              size={22}
              color="#ccc"
              title="Belum check-in"
            />
          );
        }

        return value ? (
          <MdCheckBox size={22} />
        ) : (
          <MdCheckBoxOutlineBlank
            size={22}
            style={{ cursor: "pointer", color: "#8F351C" }}
            onClick={() => handleConsumption(row.id)}
          />
        );
      }
    }
  ];

  const filteredConsumption = useMemo(() => {
    const keyword = searchTerm.toLowerCase().trim();

    const filtered = attendance.filter((item) => {
      if (!keyword) return true;

      const nameMatch = item.guest?.name
        ?.toLowerCase()
        .includes(keyword);

      const phone = item.guest?.phone_number || "";
      const last4 = phone.slice(-4);

      const phoneMatch = last4.includes(keyword);

      return nameMatch || phoneMatch;
    });

    return [...filtered].sort((a, b) => {

      // 1️⃣ hadir dulu
      if (a.is_attend !== b.is_attend) {
        return b.is_attend - a.is_attend;
      }

      // 2️⃣ kalau sama-sama hadir / tidak hadir → sort nama
      return (a.guest?.name || "").localeCompare(b.guest?.name || "");

    });

  }, [attendance, searchTerm]);

  const paginatedConsumption = useMemo(() => {
    if (limit === "All") return filteredConsumption;

    const start = (currentPage - 1) * limit;
    const end = start + limit;

    return filteredConsumption.slice(start, end);
  }, [filteredConsumption, currentPage, limit]);

  const consumptionDataFormatted = useMemo(() => {
    return paginatedConsumption.map((item, index) => ({
      no:
        limit === "All"
          ? index + 1
          : (currentPage - 1) * limit + index + 1,

      id: item.id,
      guest_name: item.guest?.name || "-",
      phone_number: formatPhoneNumber(
        item.guest?.phone_number,
        roleId
      ),
      is_attend: item.is_attend,
      is_consumed: item.is_consumed,
      highlight: item.id === recentConsumptionId
    }));
  }, [paginatedConsumption, roleId, currentPage, limit, recentConsumptionId]);

  return (
    <div className="event-attendance-page">
      <h2>Event Attendance</h2>
      {eventDateTime && (
        <CountdownTimer targetDate={eventDateTime} />
      )}

      <div className="tab-sheet">
        <button
          className={`tab-item ${activeTab === "detail" ? "active" : ""}`}
          onClick={() => setActiveTab("detail")}
        >
          <RiCalendarScheduleFill size={18} /> 
          <span>Rundown</span>
        </button>

        <button
          className={`tab-item ${activeTab === "attendance" ? "active" : ""}`}
          onClick={() => setActiveTab("attendance")}
        >
          <FaUserCheck size={18}/> 
          <span>Attendance</span>
        </button>

        {/**
        <button
          className={`tab-item ${activeTab === "consumption" ? "active" : ""}`}
          onClick={() => setActiveTab("consumption")}
        >
          <ImSpoonKnife size={18}/> 
          <span>Consumption</span>
        </button>
        */}
      </div>

      {loading && (
        <div className="loading">
          <p>Loading...</p>
        </div>
      )}

      {!loading && (
        <>
          {activeTab === "detail" && (
            <EventDetailSection
              rundown={rundown}
              eventDetail={eventDetail}
            />
          )}

          {activeTab === "attendance" && (
            <div className="attendance-content">
              <div className="attendance-scanner-container">
                <CommonButton
                  className="scanner-button"
                  onClick={() => setShowScanner(!showScanner)}
                >
                  <MdQrCodeScanner className="scanner-icon" /> Scan QR
                </CommonButton>
              </div>

              {showScanner && (
                <div className="scanner-overlay">
                  <div className="scanner-modal">
                    <QRScanner onScanSuccess={handleQRScan} />

                    <button
                      className="scanner-close-btn"
                      onClick={() => setShowScanner(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              <div className="attendance-table-section">
                <div style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "12px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  marginBottom: "20px"
                }}>
                  <Chart
                    data={[
                      { name: "Hadir", value: attendanceSummary.totalAttend, filterKey: "attend" },
                      { name: "Tidak Hadir", value: attendanceSummary.totalNotAttend, filterKey: "notAttend" }
                    ]}
                    total={attendanceSummary.totalGuests}
                    selectedFilter={attendanceFilter}
                    onFilterSelect={handleAttendanceFilter}
                  />
                </div>
                <div className="attendance-table-header">
                  <button
                    className="download-icon-btn"
                    onClick={handleDownloadExcel}
                  >
                    Download <MdDownload size={22} />
                  </button>
                </div>
                <div className="attendance-controls">
                  <CustomFormGroup
                    type="select"
                    options={limitOptions}
                    value={limit}
                    onChange={handleLimitChange}
                    icon={<FaList />}
                  />

                  <CustomFormGroup
                    type="text"
                    placeholder="Search by name, phone, or seat"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    icon={<FaSearch />}
                  />
                </div>
                <Table
                  columns={attendanceColumns}
                  data={attendanceDataFormatted}
                  onFiltersChange={(filters) => {
                    setTableFilters(filters);
                    setCurrentPage(1);
                  }}
                />
                <Pagination
                  currentPage={currentPage}
                  totalPages={
                    limit === "All"
                      ? 1
                      : Math.ceil(filteredAttendance.length / limit)
                  }
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            </div>
          )}

          {activeTab === "consumption" && (
            <div className="consumption-content">
              <div className="consumption-scanner">
                <CommonButton 
                  className="scanner-button"
                  onClick={() => setShowConsumptionScanner(true)}>
                  <ImSpoonKnife className="scanner-icon" /> Scan QR <MdQrCodeScanner/>
                </CommonButton>
              </div>

              <div className="consumption-summary">
                <Chart
                  data={[
                    { name: "Dikonsumsi", value: consumptionSummary.totalConsumed },
                    { name: "Tidak Dikonsumsi", value: consumptionSummary.totalNotConsumed }
                  ]}
                  total={consumptionSummary.totalGuests}
                />
              </div>
              <div className="attendance-controls">
                <CustomFormGroup
                  type="select"
                  options={limitOptions}
                  value={limit}
                  onChange={handleLimitChange}
                  icon={<FaList />}
                />

                <CustomFormGroup
                  type="text"
                  placeholder="Search by name or phone"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  icon={<FaSearch />}
                />
              </div>
              <Table
                columns={consumptionColumns}
                data={consumptionDataFormatted}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={
                  limit === "All"
                    ? 1
                    : Math.ceil(filteredConsumption.length / limit)
                }
                onPageChange={(page) => setCurrentPage(page)}
              />
              {showConsumptionScanner && (
                <div className="scanner-overlay">
                  <div className="scanner-modal">
                    <QRScanner onScanSuccess={handleConsumptionQR} />

                    <button
                      className="scanner-close-btn"
                      onClick={() => setShowConsumptionScanner(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {isModalOpen && (
        <MessageModal
          onClose={handleClose}
          title={isSuccess ? "Success" : "Failed"}
          message_content={modalMessage}
          button_name="Close"
          isSuccess={isSuccess}
          icon={isSuccess ? <FaCheckCircle /> : <FaTimesCircle />}
        />
      )}
    </div>
  );
};

export default EventAttendancePage;
